/**
 * ClientRecordAccessCard: the owner's "Who can open client records"
 * switch, at the top of Admin › Permissions, above the matrix.
 *
 * Two choices, "Everyone on staff" and "Each person’s own service",
 * stored at tenants.config.access.client_records and applied by the
 * server on the next request. Like every toggle on the tab it applies on
 * the click, with no confirm: the radios are disabled while the save is
 * pending and return to the server's value when it fails.
 *
 * The card saves from each radio's click, not its change. A click on the
 * option that is not checked saves it. A click on the checked option
 * saves nothing, except while the stored value is unrecognised (the GET's
 * stored_value_recognised false, shown as a notice): then a click on the
 * checked "Each person’s own service" saves own_service, so an owner
 * repairs the setting without ever widening it. The repair pushes no undo,
 * since the value in force before it is the one it saved.
 *
 * After a save the card refetches its GET and whoami, so the owner's own
 * menus update at once. Undo puts the previous value back with a PUT and
 * never removes the key. A save answering changed: false (another owner
 * had already saved that value) pushes no undo, since undoing it would
 * overwrite that owner's save under this owner's name.
 *
 * The option descriptions, help lines and notice run the card's width up
 * to a 75ch measure.
 *
 * The heading, options, help lines, notice and undo label come from
 * CLIENT_RECORD_ACCESS_COPY (src/lib/clientRecordAccess.ts), which the
 * matrix's lane blurb is built from too.
 */

import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { useTenant } from '@/lib/auth';
import { useUndo } from '@/lib/undo';
import { contactSentence, errorText, serverSentence, sessionExpiredText } from '@/lib/errorCopy';
import { formatTimestamp } from '@/lib/format';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  CLIENT_RECORD_ACCESS_COPY,
  CLIENT_RECORD_ACCESS_VALUES,
  type ClientRecordAccess,
} from '@/lib/clientRecordAccess';
import {
  clientRecordAccessKey,
  fetchClientRecordAccess,
  saveClientRecordAccess,
  type ClientRecordAccessSetting,
} from '@/lib/clientRecordAccessApi';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';

export interface ClientRecordAccessCardProps {
  tenantSlug: string;
}

const FALLBACK_ERROR =
  "That setting didn't save, and it's unchanged. Try again, and if it happens again, tell {contact_system}.";

function lastChangedLine(setting: ClientRecordAccessSetting): string | null {
  const last = setting.last_changed;
  if (!last) return null;
  const date = formatTimestamp(last.at);
  const name = last.by_display_name?.trim();
  return name ? `Changed by ${name} on ${date}` : `Changed on ${date}`;
}

export function ClientRecordAccessCard({ tenantSlug }: ClientRecordAccessCardProps): ReactNode {
  const { refreshWhoami } = useTenant();
  const { push: pushUndo } = useUndo();
  const contact = useSupportContact();
  const queryClient = useQueryClient();
  const queryKey = clientRecordAccessKey(tenantSlug);

  const query = useQuery<ClientRecordAccessSetting, Error>({
    queryKey,
    queryFn: ({ signal }) => fetchClientRecordAccess(tenantSlug, signal),
    staleTime: 15_000,
  });

  const [pendingValue, setPendingValue] = useState<ClientRecordAccess | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const setting = query.data;
  const checkedValue = pendingValue ?? setting?.value ?? null;

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey });
    await refreshWhoami?.();
  }

  // A refusal cannot succeed on a retry, so it never says "Try again";
  // a malformed stored setting carries the server's own sentence (a code
  // listed in SERVER_SENTENCE_CODES); anything else is the tab's fallback
  // sentence.
  function saveErrorText(e: unknown): string {
    const expired = sessionExpiredText(e);
    if (expired !== null) return expired;
    if (e instanceof ApiError && e.status === 403) return errorText('page_write', 'forbidden', contact);
    if (e instanceof ApiError && e.status === 409) {
      const sentence = serverSentence(e, 'record_access');
      if (sentence !== null) return sentence;
    }
    return contactSentence(FALLBACK_ERROR, contact);
  }

  async function save(value: ClientRecordAccess, current: ClientRecordAccessSetting): Promise<void> {
    const repair = current.stored_value_recognised === false && value === current.value;
    const previous = current.value;
    setErrorMsg(null);
    setPendingValue(value);
    try {
      const result = await saveClientRecordAccess(tenantSlug, value);
      await refresh();
      if (result.changed && !repair) {
        pushUndo({
          label: CLIENT_RECORD_ACCESS_COPY.undoLabel(CLIENT_RECORD_ACCESS_COPY.options[value].label),
          undo: async () => {
            await saveClientRecordAccess(tenantSlug, previous);
            await refresh();
          },
        });
      }
    } catch (e) {
      setErrorMsg(saveErrorText(e));
    } finally {
      setPendingValue(null);
    }
  }

  function onOptionClick(value: ClientRecordAccess): void {
    if (!setting || pendingValue !== null) return;
    const isChecked = value === setting.value;
    // The checked option saves only as the repair of an unrecognised value.
    if (isChecked && !(setting.stored_value_recognised === false && value === 'own_service')) return;
    void save(value, setting);
  }

  const copy = CLIENT_RECORD_ACCESS_COPY;
  const lastLine = setting ? lastChangedLine(setting) : null;

  return (
    <section
      data-testid="client-record-access"
      className="mb-5 bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 px-4 py-4"
    >
      {query.isLoading && (
        <div>
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && (
        <ErrorBanner error={query.error} onRetry={() => query.refetch()} what="the client record setting" />
      )}

      {setting && (
        <>
          <fieldset>
            <legend className="font-display text-lg text-sage-800">{copy.heading}</legend>
            <div className="mt-3 space-y-3">
              {CLIENT_RECORD_ACCESS_VALUES.map((value) => {
                const option = copy.options[value];
                const inputId = `client-record-access-${value}`;
                return (
                  <div key={value}>
                    <label htmlFor={inputId} className="flex items-start gap-2 cursor-pointer min-h-11">
                      <input
                        id={inputId}
                        type="radio"
                        name="client-record-access"
                        value={value}
                        data-testid={`client-record-access-option-${value}`}
                        className="mt-1"
                        checked={checkedValue === value}
                        disabled={pendingValue !== null}
                        onClick={() => onOptionClick(value)}
                        onChange={() => {}}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-ink font-medium">{option.label}</span>
                        <span className="block text-xs text-ink-soft mt-0.5 max-w-[75ch]">{option.subline}</span>
                        {value === 'own_service' && (
                          <>
                            <span className="block text-xs text-ink-soft mt-0.5 max-w-[75ch]">
                              {copy.options.own_service.help}
                            </span>
                            <span className="block text-xs text-ink-soft mt-0.5 max-w-[75ch]">
                              {copy.options.own_service.screens}
                            </span>
                          </>
                        )}
                      </span>
                    </label>
                    {value === 'own_service' && setting.stored_value_recognised === false && (
                      <p
                        data-testid="client-record-access-unrecognised"
                        className="mt-1 ml-6 px-3 py-2 text-xs bg-warm-50 border border-warm-300 text-ink rounded-md max-w-[75ch]"
                      >
                        {copy.unrecognised}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </fieldset>

          <p className="text-xs text-ink-soft mt-3 max-w-[75ch]">{copy.help}</p>

          {lastLine !== null && (
            <p data-testid="client-record-access-last-changed" className="text-xs text-ink-soft mt-1">
              {lastLine}
            </p>
          )}

          {errorMsg && (
            <div
              role="alert"
              data-testid="client-record-access-error"
              className="mt-3 px-3 py-2 text-sm bg-warm-50 border border-warm-300 text-ink rounded-md"
            >
              {errorMsg}
            </div>
          )}
        </>
      )}
    </section>
  );
}
